import React, { useEffect, useState } from 'react';
import { adminListUserFactors, adminResetUserMfa } from '../../utils/adminMfaApi';

/**
 * AdminResetMfaDialog
 *   targetUser: { id, email, name }
 *   onClose(): void
 *   onDone(): void  — parent re-fetches users
 *
 * Requires the admin to type the target user's email exactly before the
 * destructive button enables. Cuts the "accidental click" failure mode.
 */
export default function AdminResetMfaDialog({ targetUser, onClose, onDone }) {
  const [factors, setFactors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await adminListUserFactors(targetUser.id);
        if (!cancelled) setFactors(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetUser.id]);

  const confirmMatches =
    confirmEmail.trim().toLowerCase() === (targetUser.email || '').trim().toLowerCase();

  const handleReset = async () => {
    setSubmitting(true);
    setError('');
    try {
      await adminResetUserMfa(targetUser.id);
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Reset failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Reset two-factor authentication
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          This will <strong>disable all MFA factors</strong> for{' '}
          <span className="font-medium">{targetUser.name || targetUser.email}</span>. They will
          be able to sign in with just their password until they re-enroll.
        </p>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : factors && factors.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded text-sm mb-4">
            This user has no active MFA factors. Nothing to reset.
          </div>
        ) : factors ? (
          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm mb-4">
            <div className="font-medium text-gray-800 mb-1">Active factors:</div>
            <ul className="list-disc list-inside text-gray-700">
              {factors.map((f) => (
                <li key={f.factor_id}>{f.factor_type}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Type <span className="font-mono">{targetUser.email}</span> to confirm
          </label>
          <input
            type="text"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
            placeholder={targetUser.email}
            disabled={submitting}
          />
        </div>

        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-3"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={submitting || !confirmMatches || (factors && factors.length === 0)}
            className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Resetting…' : 'Disable MFA'}
          </button>
        </div>
      </div>
    </div>
  );
}
